import './SortableList.css';
import React, { useState, ReactNode } from 'react';
import { sortBy as lodashSortBy, compact } from 'lodash';
import classnames from 'classnames';
import GlobalProps from './../globalProps';
import { toggleElement, transpose } from '../../util/util';
import { Spinner } from '../Spinner/Spinner';
import { t } from '../../locales/translate';
import { ScrollWrapper } from '../ScrollWrapper/ScrollWrapper';
import { SpacingSizes, Stack } from '../Stack/Stack';
import { ColumnHeader } from './ColumnHeader';
import { TableCell } from './TableCell';
import { MultiSelectCell, MultiSelectHeader } from './MultiSelect';
import { ExpandableCell, ExpandableHeader } from './Expandable';
import * as PopperJS from 'popper.js';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface ColumnContent<T> {
  width?: number | string;
  maxWidth?: number | string;
  align?: 'left' | 'center' | 'right';
  labelAlign?: 'left' | 'center' | 'right';
  label?: React.ReactNode;
  dataKey: Extract<keyof T, string>;
  keyOverride?: string;
  sortable?: boolean;
  highlight?: boolean;
  disableLink?: boolean;
  separator?: boolean;
  apiSortIcon?: ReactNode;
  renderedKeyOverride?: (data: T, index: number) => number | string;
  sortRenderer?: (data: T, index: number) => number | string;
  cellRenderer?: (data: T, index: number) => React.ReactNode;
  footer?: ReactNode;
  expandedOnMount?: boolean;
  tooltip?: {
    content?: string | ReactNode;
    position?: PopperJS.Placement;
  };
  ellipsisOverflow?: boolean;
  cellClassNames?: string[] | ((data: T, index: number) => string[]);
  rowSpan?: (data: T, index: number) => number;
}

interface Props<T> extends GlobalProps {
  fullBleed?: boolean;
  items?: T[];
  columns: Array<ColumnContent<T> | null | false>;
  defaultSort?: { sortBy: Extract<keyof T, string>; sortAscending: boolean };
  size?: SpacingSizes;
  actions?: React.ReactNode;
  loading?: boolean;
  error?: Error;
  multiSelect?: boolean;
  selectedItems?: T[];
  verticalAlign?: 'middle' | 'top' | 'bottom';
  emptyMessage?: string;
  scrollPadding?: string;
  rightSpacing?: string | number;
  compactExpandableContent?: boolean;
  switchRowsAndColumns?: boolean;
  footerLabel?: ReactNode;
  condensed?: boolean;
  onRowClick?: (data: T, index: number) => void;
  onRowSelect?: (data: T, index: number) => void;
  rowLink?: (data: T) => string;
  expandable?: (data: T) => boolean;
  onExpand?: (data: T) => void;
  expandedContent?: (data: T) => React.ReactNode;
  onSelectAll?: () => void;
  rowClass?: (data: T) => string | undefined | null | boolean;
  disableSelectForRow?: (data: T) => boolean;
  forceCheckForRow?: (data: T) => boolean;
  partiallyCheckedRow?: (data: T) => boolean;
  mutedRow?: (data: T) => boolean;
  fullWidth?: boolean;
  showLoadMoreSpinner?: boolean;
}

export function SortableList<T extends {}>({
  loading,
  columns: rawColumns,
  fullBleed,
  multiSelect,
  selectedItems,
  verticalAlign = 'middle',
  className,
  style,
  items = [],
  defaultSort,
  size = 'default',
  actions,
  error,
  emptyMessage,
  scrollPadding = 'var(--spacing-md)',
  rightSpacing,
  switchRowsAndColumns,
  compactExpandableContent,
  footerLabel,
  condensed,
  onRowClick,
  onRowSelect,
  expandable,
  onExpand,
  expandedContent,
  rowLink,
  onSelectAll,
  rowClass,
  disableSelectForRow = () => false,
  forceCheckForRow = () => false,
  partiallyCheckedRow = () => false,
  mutedRow = () => false,
  fullWidth,
  showLoadMoreSpinner,
}: Props<T>): JSX.Element {
  const [sortBy, setSortBy] = useState(defaultSort ? defaultSort.sortBy : undefined);
  const [sortAscending, setSortAscending] = useState(defaultSort ? defaultSort.sortAscending : true);
  const columns = compact(rawColumns);
  // Get the index number of column items which have initExpanded: true;
  const expandedColumnsOnMount = columns
    .map((item, index) => (item.expandedOnMount ? index : -1))
    .filter((item) => item >= 0);
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>(expandedColumnsOnMount || []);

  const expandedItems = expandedIndexes.map((e) => items[e]);

  const sortedItems: Array<T> = compact(
    (() => {
      if (!sortBy) {
        return items;
      }
      const sortColumn = columns.find((e) => !e.apiSortIcon && e.dataKey === sortBy);
      if (!sortColumn) {
        return items;
      }
      const sortKey = sortColumn.sortRenderer || sortBy;
      const result = lodashSortBy(items, sortKey);
      if (!sortAscending) {
        result.reverse();
      }
      return result;
    })()
  );

  function onColumnClick(columnKey: Extract<keyof T, string>): void {
    const column = columns.find((e) => e.dataKey === columnKey);
    if (!column || !column.sortable) {
      return;
    }

    if (!sortBy) {
      setSortBy(columnKey);
      setSortAscending(true);
    } else if (sortAscending) {
      setSortAscending(false);
    } else {
      setSortBy(undefined);
    }
  }

  function handleOnRowCheck(row: T, index: number): void {
    onRowSelect?.(row, index);
  }

  const expandableItemsCount = expandable ? items.filter(expandable).length : 0;

  let cells: React.ReactNode[][] = [];

  // headers
  cells.push(
    columns.map((column) => (
      <ColumnHeader key={column.keyOverride || column.dataKey} {...{ column, sortBy, sortAscending, onColumnClick }} />
    ))
  );

  // Cells
  sortedItems.forEach((row, index) => {
    cells.push(
      columns.map((column) => (
        <TableCell
          key={column.renderedKeyOverride?.(row, index) || column.keyOverride || column.dataKey}
          {...{
            row,
            index,
            column,
            rowLink,
            sortBy,
            onRowClick,
            mutedColor: mutedRow(row),
          }}
        />
      ))
    );
  });

  if (switchRowsAndColumns) {
    cells = transpose(cells);
  }

  // Expandable
  if (expandableItemsCount > 0) {
    cells[0].unshift(
      <ExpandableHeader
        key="meta_fields"
        onSetExpandedIndexes={(value) => setExpandedIndexes(value)}
        {...{ items, expandedItems, expandableItemsCount, expandable }}
      />
    );
    sortedItems.forEach((row, index) => {
      cells[index + 1].unshift(
        <ExpandableCell
          key={index + 1}
          onClick={() => {
            setExpandedIndexes((state) => toggleElement(state, items.indexOf(row)));
            if (onExpand) {
              onExpand(row);
            }
          }}
          {...{
            row,
            expandedItems,
            expandableItemsCount,
            expandable,
            mutedColor: disableSelectForRow(row) && forceCheckForRow(row),
          }}
        />
      );
    });
  }

  // Multiselect
  if (multiSelect) {
    cells[0].unshift(
      <MultiSelectHeader
        key="multi_select_header"
        {...{ items, sortedItems, selectedItems, disableSelectForRow, onSelectAll }}
      />
    );
    sortedItems.forEach((row, index) => {
      cells[index + 1].unshift(
        <MultiSelectCell
          key={`multi_select_${index + 1}`}
          {...{
            row,
            index,
            selectedItems,
            disableSelectForRow,
            handleOnRowCheck,
            forceCheckForRow,
            partiallyCheckedRow,
          }}
        />
      );
    });
  }

  const anyFooter = columns.some((column) => !!column.footer);

  if (anyFooter && !multiSelect) {
    cells[0].unshift(
      <th
        key="any_footer_no_multi_select"
        className={classnames('sortable-list__header sortable-list__multiselect-column')}
      ></th>
    );
    sortedItems.forEach((_, index) => {
      cells[index + 1].unshift(
        <td className={classnames('sortable-list__cell sortable-list__multiselect-column')}></td>
      );
    });
  }

  if (rightSpacing) {
    cells[0].push(
      <th key="right_spacing--initial" className="sortable-list--spacer" style={{ width: rightSpacing }}></th>
    );
    cells.forEach((row, index) => {
      row.push(
        <th key={`right_spacing--${index}`} className="sortable-list--spacer" style={{ width: rightSpacing }}></th>
      );
    });
  }

  return (
    <div
      className={classnames('sortable-list__container', {
        'sortable-list__container--fullwidth': fullWidth,
      })}
    >
      <div className="sortable-list__actions">{actions}</div>
      <ScrollWrapper className="sortable-list__table-wrapper" direction="horizontal" padding={scrollPadding}>
        {error && (
          <div className="sortable-list__error">
            <p>{t.errors.errorLoadingList}</p>
            <pre>{error.name}</pre>
            <pre>{error.message}</pre>
          </div>
        )}
        {loading && (
          <div className="sortable-list__loading">
            <Spinner color="var(--color-tint)" />
          </div>
        )}
        {!loading && !error && (
          <Stack size="sm">
            {items.length === 0 && !loading && (
              <div className="sortable-list__empty">
                {emptyMessage || t.errors.noItemsforThisCollection(t.model.item._plural, t.general.collection)}
              </div>
            )}

            {items.length > 0 && (
              <table
                className={classnames(
                  'sortable-list',
                  `sortable-list--${size}`,
                  {
                    'sortable-list--fullbleed': fullBleed,
                    'sortable-list--expandable': expandable,
                    'sortable-list--condensed': condensed,
                  },
                  className
                )}
                style={style}
              >
                <thead>
                  <tr>{cells.shift()}</tr>
                </thead>
                <tbody>
                  {cells.map((row, index) => {
                    const data = sortedItems[index];
                    return (
                      <React.Fragment key={index}>
                        <tr
                          className={classnames(
                            'sortable-list__row',
                            {
                              'sortable-list__row--top': verticalAlign === 'top',
                              'sortable-list__row--middle': verticalAlign === 'middle',
                            },
                            (data as T & { className: string })?.className,
                            rowClass?.(data)
                          )}
                        >
                          {row}
                        </tr>
                        {expandable && expandable(data) && (
                          <tr
                            className={classnames('sortable-list__row', 'sortable-list__row--expansion', {
                              'sortable-list__row--expanded': expandedItems.includes(data),
                            })}
                          >
                            <td />
                            <td colSpan={columns.length + 1}>
                              <Collapse open={expandedItems.includes(data)}>
                                {() =>
                                  expandedContent && (
                                    <div
                                      className={classnames('sortable-list__expansion-content', {
                                        'sortable-list__expansion-content--compact': compactExpandableContent,
                                      })}
                                    >
                                      {expandedContent(data)}
                                    </div>
                                  )
                                }
                              </Collapse>
                            </td>
                            {rightSpacing && (
                              <th
                                key="right_spacing"
                                className="sortable-list--spacer"
                                style={{ width: rightSpacing }}
                              ></th>
                            )}
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {anyFooter && (
                    <tr className="sortable-list__footer">
                      <td colSpan={compact([multiSelect, expandable]).length} className="sortable-list__cell">
                        {footerLabel}
                      </td>
                      {columns.map((column) => (
                        <td key={column.dataKey} className="sortable-list__cell">
                          {column.footer}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            <Stack justify="center">{items.length > 0 && showLoadMoreSpinner && <Spinner />}</Stack>
          </Stack>
        )}
      </ScrollWrapper>
    </div>
  );
}
